import React, { useState, useEffect } from "react";
import Image from "../images/Image";
import SidebarItem from "./SidebarItem";
import { sidebarObj } from "./SidebarObj";
import { useLocation } from "react-router-dom";
import { getFirefoxCssFix } from "../commonfunctions/getFirefoxCssFix";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import usePrivilege from "../CustomHooks/usePrivilege";

interface DropdownItem {
  option: string;
  path: string;
  privilege: string;
}

interface SidebarItemType {
  label: string;
  dropdown?: string[] | DropdownItem[];
  link?: string;
  privileges?: string | string[];
}

interface ActiveTabState {
  activeTab: string;
  dropdownOpen: boolean;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { privileges } = useSelector((state: RootState) => state.privileges);
  const hasListMDPrivilege = usePrivilege('MDM1')
  const hasAddMDPrivilege = usePrivilege('MDM4')
  const [isActiveTab, setIsActiveTab] = useState<ActiveTabState>({
    activeTab: location.pathname,
    dropdownOpen: false,
  });
  const [dropdownOptions, setDropdownOptions] = useState<Record<string, boolean>>({});
  
  function privilegesCheck(user:any, requiredPrivileges = []) {
    // Assume user object contains an array of assigned privileges
    if (!user || !Array.isArray(user.privileges)) {
      return false;
    }
  
    const assigned = user.privileges;
    const missingPrivileges = [];
  
    for (let i = 0; i < requiredPrivileges.length; i++) {
      const required = requiredPrivileges[i];
  
      if (!assigned.includes(required)) {
        missingPrivileges.push(required);
      }
    }
  
    // If no missing privileges, user is granted access
    if (missingPrivileges.length === 0) {
      return true;
    }
  
    // Optional: Check if the user has an override access
    if (user.overrideAccess) {
      return true;
    }
  
    return false;
  }
  
  useEffect(() => {
    // Initialize dropdown options state
    const initializeDropdownOptions = () => {
      const optionsMap: Record<string, boolean> = {};
      
      // Process all items in sidebarObj
      sidebarObj.forEach(item => {
        // Handle dropdown items
        if (Array.isArray(item.dropdown)) {
          item.dropdown.forEach(dropdownItem => {
            // Handle object type dropdown items with 'option' property
            if (typeof dropdownItem === 'object' && 'option' in dropdownItem) {
              optionsMap[dropdownItem.option] = false;
            }
            // Handle string type dropdown items
            else if (typeof dropdownItem === 'string') {
              optionsMap[dropdownItem] = false;
            }
          });
        }
      });
      
      setDropdownOptions(optionsMap);
    };
    
    initializeDropdownOptions();
  }, []);
  
  useEffect(() => {
    setIsActiveTab((prev) => ({
      ...prev,
      activeTab: location.pathname,
    }));
  }, [location]);

  const checkPrivileges = (itemPrivileges: string | string[] | undefined): boolean => {
    // Return false if no privileges are specified
    if (!itemPrivileges || !privileges) {
      return false;
    }
    if(privileges === 'superadmin'){
      return true;
    }
    // Handle 'allowed' case
    if (itemPrivileges === 'allowed') {
      return true;
    }

    // Handle array of privileges
    if (Array.isArray(itemPrivileges)) {
      return itemPrivileges.some(privilege => privileges.includes(privilege));
    }
    // Handle single privilege string
    return privileges.includes(itemPrivileges);
  };

  const checkDropdownPrivileges = (item: SidebarItemType): boolean => {
    // Check the main item's privileges first
    if (item.label === "User Management" && Array.isArray(item.dropdown)) {
      const dropdownItems = item.dropdown as DropdownItem[];
      return dropdownItems.some((dropdownItem) => 
        'privilege' in dropdownItem && checkPrivileges(dropdownItem.privilege)
      );
    } else if (item.label !== "Dashboard" && !checkPrivileges(item.privileges)) {
      return false;
    }
    
    // If there's no dropdown, return true since main privileges passed
    // if (!item.dropdown) {
    //   return true;
    // }

    // For other items with dropdown
    const filteredDropdown = item.dropdown.filter(
      (dropdownItem) => typeof dropdownItem === "string"
    );

    if (filteredDropdown.length === 0) {
      return false;
    }

    return filteredDropdown.some((dropdownItem) =>
      typeof dropdownItem === "string" ? true : 'privilege' in dropdownItem && privileges.includes(dropdownItem.privilege)
    );
  };
  const renderSidebarItems = () => {
    return sidebarObj
      .filter(item => item !== undefined)
      .map((item, index) =>
        checkPrivileges(item.privileges) ? (
          <SidebarItem
            key={`${item.label}-${index}`}
            item={item}
            isActiveTab={isActiveTab}
            setIsActiveTab={setIsActiveTab}
            hasADDMDPrivilege={hasAddMDPrivilege}
            hasListMDPrivilege={hasListMDPrivilege}
            dropdownOptions={dropdownOptions}
            setDropdownOptions={setDropdownOptions}
          />
        ) : null
      );
  };

  return (
    <div className="bg-white w-72 h-screen px-3 transition-transform duration-200 ease-in-out">
      <div className="flex items-center justify-center mt-[40px] mb-2.5">
        <Image src="zood_pay_sidebar_img" />
      </div>
      <ul className={`overflow-y-scroll overflow-x-hidden ${getFirefoxCssFix('scroll')} scroll-auto h-scrollbarheight`}>
        {renderSidebarItems()}
      </ul>
    </div>
  );
};

export default Sidebar;